import React, { useState, useEffect } from 'react';
import { getAppeals, getFineByAuto } from '../api';
import './AppealHistory.css';

function AppealHistory({ onNavigate }) {
  const empresaId = localStorage.getItem('empresaId') || 'default';

  const [appeals, setAppeals]                 = useState([]);
  const [filteredAppeals, setFilteredAppeals] = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [loadingProcess, setLoadingProcess]   = useState(null); // auto sendo carregado
  const [filters, setFilters]                 = useState({ status: '', prazo: '', busca: '' });

  useEffect(() => { loadAppeals(); }, []);
  useEffect(() => { applyFilters(); }, [appeals, filters]);

  const loadAppeals = async () => {
    setLoading(true);
    try {
      const data = await getAppeals(empresaId);
      setAppeals(data);
    } catch (e) {
      console.error('Erro ao carregar recursos:', e);
    }
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...appeals];
    if (filters.status)
      filtered = filtered.filter(a =>
        (a.status || '').toLowerCase().includes(filters.status.toLowerCase())
      );
    if (filters.prazo)
      filtered = filtered.filter(a => a.prazo && a.prazo <= filters.prazo);
    if (filters.busca)
      filtered = filtered.filter(a =>
        (a.auto || '').toLowerCase().includes(filters.busca.toLowerCase()) ||
        (a.placa || '').toLowerCase().includes(filters.busca.toLowerCase())
      );
    setFilteredAppeals(filtered);
  };

  const clearFilters = () => setFilters({ status: '', prazo: '', busca: '' });

  const getPrazoStatus = (prazo) => {
    if (!prazo) return 'normal';
    const diff = Math.ceil((new Date(prazo) - new Date()) / 86_400_000);
    if (diff < 0)   return 'vencido';
    if (diff <= 5)  return 'urgente';
    if (diff <= 15) return 'proximo';
    return 'normal';
  };

  const isExpired = (prazo) => {
    if (!prazo) return false;
    const [d, m, y] = prazo.split('/');
    return new Date(y, m - 1, d) < new Date();
  };

  const STATUS_LABEL = {
    em_analise:          'Em análise',
    defesa_gerada:       'Defesa Gerada',
    aguardando_resposta: 'Aguardando Resposta',
    segunda_instancia:   'Segunda Instância',
    concluido:           'Concluído'
  };

  // Buscar dados completos da multa e abrir o processo
  const handleRowClick = async (appeal) => {
    setLoadingProcess(appeal.auto);
    try {
      const fine = await getFineByAuto(empresaId, appeal.auto);
      if (!fine) {
        alert(`Dados da multa não encontrados para o auto ${appeal.auto}`);
        return;
      }

      // Montar objeto igual ao usado pelo "Ver Processo" da Lista de Multas
      const processData = encodeURIComponent(JSON.stringify({
        ...fine,
        id:              appeal.id || appeal.auto,
        orgao:           fine.concessionaria,
        fase:            appeal.fase || 'defesa_previa',
        status:          appeal.status,
        responsavel:     appeal.responsavel || '',
        prioridade:      appeal.prioridade  || 'Normal',
        prazo:           fine.deadline,
        deadlineExpired: isExpired(fine.deadline)
      }));

      window.open(
        `${window.location.origin}${window.location.pathname}?process=${processData}`,
        '_blank'
      );
    } catch (e) {
      alert('Erro ao carregar dados do processo: ' + e.message);
    } finally {
      setLoadingProcess(null);
    }
  };

  if (loading) return <div className="loading">Carregando recursos...</div>;

  return (
    <div className="appeal-history">
      <div className="filters-card">
        <h3>Filtros</h3>
        <div className="filters-grid">
          <div className="filter-item">
            <label>Status:</label>
            <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
              <option value="">Todos</option>
              <option value="em_analise">Em análise</option>
              <option value="defesa_gerada">Defesa Gerada</option>
              <option value="aguardando_resposta">Aguardando Resposta</option>
              <option value="segunda_instancia">Segunda Instância</option>
              <option value="concluido">Concluído</option>
            </select>
          </div>
          <div className="filter-item">
            <label>Prazo até:</label>
            <input type="date" value={filters.prazo}
              onChange={e => setFilters({ ...filters, prazo: e.target.value })} />
          </div>
          <div className="filter-item">
            <label>Auto / Placa:</label>
            <input type="text" placeholder="Buscar..."
              value={filters.busca}
              onChange={e => setFilters({ ...filters, busca: e.target.value })} />
          </div>
          <div className="filter-item">
            <button onClick={clearFilters} className="clear-btn">Limpar</button>
          </div>
        </div>
      </div>

      <div className="appeal-card">
        <div className="card-header">
          <h2>Recursos ({filteredAppeals.length})</h2>
          <button onClick={loadAppeals} className="refresh-btn">🔄 Atualizar</button>
        </div>

        {filteredAppeals.length === 0 ? (
          <div className="empty-state">
            <span>📭</span>
            <p>Nenhum recurso encontrado.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Auto</th>
                  <th>Placa</th>
                  <th>Linha</th>
                  <th>Fase</th>
                  <th>Status</th>
                  <th>Instância</th>
                  <th>Prazo</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {filteredAppeals.map((appeal, idx) => (
                  <tr key={idx}
                    onClick={() => handleRowClick(appeal)}
                    className={`clickable-row ${loadingProcess === appeal.auto ? 'loading-row' : ''}`}>
                    <td>
                      <span className="auto-link">
                        {loadingProcess === appeal.auto ? '⏳ ' : ''}{appeal.auto}
                      </span>
                    </td>
                    <td>{appeal.placa || '—'}</td>
                    <td>{appeal.linha ? appeal.linha.split('-')[0].trim() : '—'}</td>
                    <td>{appeal.fase === 'defesa_previa' ? 'Defesa Prévia' : '2ª Instância'}</td>
                    <td>
                      <span className={`status status-${(appeal.status || '').replace('_', '-')}`}>
                        {STATUS_LABEL[appeal.status] || appeal.status}
                      </span>
                    </td>
                    <td>{appeal.instancia}ª</td>
                    <td>
                      {appeal.prazo ? (
                        <span className={`prazo prazo-${getPrazoStatus(appeal.prazo)}`}>
                          {appeal.prazo}
                        </span>
                      ) : '—'}
                    </td>
                    <td>{appeal.valor || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AppealHistory;